import React, { useMemo, useState } from 'react';
import { message } from 'antd';
import { useAuth } from '../../../contexts/AuthContext';
import useAdminUsers from './hooks/useAdminUsers';
import useAppSettings from './hooks/useAppSettings';
import ImpersonationBanner from './ImpersonationBanner';
import UsersToolbar from './UsersToolbar';
import UsersTable from './UsersTable';
import UserFormModal from './UserFormModal';
import RegistrationSettings from './RegistrationSettings';
import styles from './Admin.module.scss';

export default function Admin() {
    const { user, impersonation, startImpersonation, stopImpersonation } = useAuth();
    const { users, loading, fetchUsers, saveUser, deleteUser, toggleAdmin } = useAdminUsers();
    const { settings, loading: settingsLoading, setRegistrationEnabled } = useAppSettings();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [searchText, setSearchText] = useState('');

    const filteredUsers = useMemo(() => {
        const query = searchText.toLowerCase();
        return users.filter(u =>
            u.username.toLowerCase().includes(query) ||
            (u.email || '').toLowerCase().includes(query)
        );
    }, [users, searchText]);

    if (!user?.is_admin) {
        return <div>Forbidden</div>;
    }

    const handleCreate = () => {
        setEditingUser(null);
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingUser(record);
        setIsModalVisible(true);
    };

    const handleFormSubmit = async (values) => {
        const success = await saveUser(editingUser, values);
        if (success) setIsModalVisible(false);
    };

    const handleImpersonate = async (userId, username) => {
        const result = await startImpersonation(userId);
        if (result.success) {
            message.success(`Now impersonating ${username}`);
        } else {
            message.error(result.error);
        }
    };

    const handleStopImpersonation = async () => {
        const result = await stopImpersonation();
        if (result.success) {
            message.success('Impersonation stopped');
        } else {
            message.error(result.error);
        }
    };

    return (
        <div className={styles.admin}>
            {impersonation && (
                <ImpersonationBanner
                    targetUsername={impersonation.targetUsername}
                    onStop={handleStopImpersonation}
                />
            )}
            <div className={styles.settings}>
                <RegistrationSettings
                    registrationEnabled={settings?.registrationEnabled}
                    loading={settingsLoading}
                    onChange={setRegistrationEnabled}
                />
            </div>
            <div className={styles.toolbar}>
                <UsersToolbar
                    searchText={searchText}
                    onSearchChange={setSearchText}
                    onCreate={handleCreate}
                    onRefresh={fetchUsers}
                    refreshing={loading}
                />
            </div>
            <UsersTable
                users={filteredUsers}
                loading={loading}
                currentUserId={user.id}
                onToggleAdmin={toggleAdmin}
                onImpersonate={handleImpersonate}
                onEdit={handleEdit}
                onDelete={deleteUser}
            />
            <UserFormModal
                visible={isModalVisible}
                editingUser={editingUser}
                onSubmit={handleFormSubmit}
                onCancel={() => setIsModalVisible(false)}
            />
        </div>
    );
}
